import React, { useState, useEffect, useRef } from 'react';
import { Modal, Spin, Button, Tabs } from 'antd';
import { DownloadOutlined, CopyOutlined, FileTextOutlined, EyeOutlined } from '@ant-design/icons';
import '../style/MarkdownViewer.css'; 

const MarkdownViewerBig = ({ visible, onClose, markdownContent, reportId, reportName }) => {
    const [copied, setCopied] = useState(false);
    const [activeTab, setActiveTab] = useState('preview');
    const [parsedHtml, setParsedHtml] = useState('');
    const [isParsing, setIsParsing] = useState(false);
    const workerRef = useRef(null);

    const handleCopy = () => {
        navigator.clipboard.writeText(markdownContent).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const handleDownload = () => {
        const BOM = "\uFEFF";
        const fullContent = BOM + markdownContent;
        const blob = new Blob([fullContent], { type: 'text/markdown;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${reportId}_${reportName}.md`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    // 컴포넌트 마운트 시 Web Worker를 초기화하고, 언마운트 시 정리합니다.
    useEffect(() => {
        // Webpack 5+ 환경에서는 new URL() 구문을 사용해야 합니다.
        const worker = new Worker(new URL('markdown.worker.js', import.meta.url));
        workerRef.current = worker;

        worker.onmessage = (event) => {
            const { html, error } = event.data;
            if (html) {
                setParsedHtml(html);
            } else if (error) {
                setParsedHtml(`<p>Error parsing markdown: ${error}</p>`);
            }
            setIsParsing(false);
        };

        return () => {
            worker.terminate();
        };
    }, []);

    // markdownContent prop이 변경되면 Worker에게 파싱 작업을 요청합니다.
    useEffect(() => {
        if (visible && markdownContent && workerRef.current) {
            setIsParsing(true);
            setParsedHtml(''); // 이전 내용 초기화
            workerRef.current.postMessage({ markdownText: markdownContent });
        }
    }, [visible, markdownContent]);
  
    return (
        <Modal
            title={`Markdown 미리보기 - ${reportName}`}
            open={visible}
            onCancel={onClose}
            width="80%"
            style={{ top: 20 }}
            bodyStyle={{ height: '70vh', overflow: 'hidden' }}
            footer={[
                <Button key="copy" icon={<CopyOutlined />} onClick={handleCopy}>
                    {copied ? '복사됨!' : '복사'}
                </Button>,
                <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={handleDownload}>
                    다운로드
                </Button>,
                <Button key="close" onClick={onClose}>
                    닫기
                </Button>,
            ]}
        >
            <Tabs activeKey={activeTab} onChange={setActiveTab}>
                <Tabs.TabPane 
                    tab={<span><EyeOutlined /> 미리보기</span>} 
                    key="preview"
                >
                    <div style={{ 
                        padding: '20px',
                        backgroundColor: '#fff',
                        maxHeight: '60vh',
                        overflowY: 'auto'
                    }}>
                        {isParsing ? (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <Spin tip="보고서 내용을 렌더링 중입니다..." size="large" />
                            </div>
                        ) : (
                            <div
                                className="custom-markdown-body"
                                dangerouslySetInnerHTML={{ __html: parsedHtml }}
                            />
                        )}
                    </div>
                </Tabs.TabPane>
                <Tabs.TabPane 
                    tab={<span><FileTextOutlined /> 원본</span>} 
                    key="source"
                >
                    <pre style={{ 
                        backgroundColor: '#f5f5f5', 
                        padding: '20px', 
                        borderRadius: '4px',
                        fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
                        fontSize: '14px',
                        lineHeight: '1.6',
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        maxHeight: '60vh',
                        overflowY: 'auto'
                    }}>
                        {markdownContent}
                    </pre>
                </Tabs.TabPane>
            </Tabs>
        </Modal>
    );
};

export default MarkdownViewerBig;